import { createSelector } from "@reduxjs/toolkit";
import { inFranchiseDeliveryZoneV2 } from "../../../utils/help/delivery/delivery";
import type {
  DeliveryType,
  FranchiseDeliveryConfig,
} from "../../../utils/types";
import type { RootState } from "../app/store";

/**
 * The guest's delivery selection, derived rather than stored.
 *
 * The slice used to keep a COPY of the chosen `FranchiseDeliveryConfig` --
 * zones, polygons, fees and all -- and the fee/ETA/zone id resolved from it.
 * All of that is server data or a pure function of server data, and the whole
 * slice is persisted to localStorage, so a returning guest rehydrated whatever
 * was true the last time they used the site. `restaurantInfo` and
 * `selectedFranchise` self-heal from the page props on every load; the copy did
 * not, so an ops zone or fee edit stayed invisible to that guest indefinitely
 * -- even on a page core had correctly revalidated (prepits/project#4204).
 *
 * The fix is to stop keeping the copy. The slice now persists only what is
 * genuinely the guest's: which config they picked (`currentDeliveryConfigId`)
 * and which address they picked. Everything else is read back out of
 * `selectedFranchise.franchiseConfig.deliveryV2`, which is rebuilt from the
 * page on every load -- so staleness is not fixed here, it is unrepresentable.
 *
 * Memoised because these are read on every render of every subscriber, and
 * because `selectDeliveryZone` builds a new object: an unmemoised one would
 * hand `useSelector` a fresh reference each call and re-render forever.
 */

/**
 * Every delivery config this storefront can currently resolve, selected
 * franchise first.
 *
 * Not just the selected franchise's own list. The store never required the
 * chosen config to belong to the selected branch, and the flows rely on that:
 * the map screens re-resolve a branch when an address moves, so there is a
 * moment where the guest holds a config belonging to the branch that actually
 * covers them while another is still selected. Narrowing this to the selected
 * franchise would silently drop the selection in exactly that window.
 *
 * Selected first so a config offered by several branches resolves to the one
 * the guest is actually on.
 */
const selectDeliveryV2 = createSelector(
  [
    (state: RootState) => state.config?.selectedFranchise,
    (state: RootState) => state.config?.activeFranchises,
  ],
  (selectedFranchise, activeFranchises): FranchiseDeliveryConfig[] => [
    ...(selectedFranchise?.franchiseConfig?.deliveryV2 ?? []),
    ...(activeFranchises ?? []).flatMap(
      (franchise) => franchise?.franchiseConfig?.deliveryV2 ?? []
    ),
  ]
);

// Optional-chained on purpose. The real store always has a delivery slice, but
// these are read from components whose tests build partial states, and a
// selector that throws on a missing slice makes every one of those fixtures
// declare a slice it does not care about.
const selectCurrentDeliveryConfigId = (state: RootState): number | null =>
  state.delivery?.currentDeliveryConfigId ?? null;

const selectSelectedAddress = (state: RootState) =>
  state.delivery?.selectedAddress ?? null;

/**
 * Matched on the config's own id.
 *
 * `.find` hands back the object that is already in the store, so the reference
 * is stable for as long as the franchise is -- which is what keeps every
 * component reading this from re-rendering on unrelated state changes.
 */
export const selectCurrentDeliveryConfig: (
  state: RootState
) => FranchiseDeliveryConfig | null = createSelector(
  [selectDeliveryV2, selectCurrentDeliveryConfigId],
  (deliveryV2, id) => {
    // Checked against null rather than for truthiness: id 0 is a legitimate
    // row id, and a truthiness test would make that one config permanently
    // unresolvable while every other id worked.
    if (id === null || id === undefined) {
      return null;
    }

    return deliveryV2?.find((config) => config.id === id) ?? null;
  }
);

export const selectDeliveryType: (state: RootState) => DeliveryType | null =
  createSelector(
    [selectCurrentDeliveryConfig],
    (config) => config?.deliveryType ?? null
  );

/**
 * The zone the guest's address falls in, against the CURRENT polygons.
 *
 * Null for a flat-fee config -- those carry their fee on the config row and
 * have no zones to match -- and null with no address, which is the state before
 * the guest has committed one. The map screens resolve a zone for a pin that is
 * not an address yet from their own local map centre; that is a live preview
 * during an interaction, not something the store needs to remember.
 */
const selectDeliveryZone = createSelector(
  [selectCurrentDeliveryConfig, selectSelectedAddress],
  (config, address) => {
    if (!config?.usesMap || !address) {
      return null;
    }

    if (
      !Number.isFinite(Number(address.lat)) ||
      !Number.isFinite(Number(address.lng))
    ) {
      return null;
    }

    return inFranchiseDeliveryZoneV2(
      { lat: Number(address.lat), lng: Number(address.lng) },
      config.deliveryConfigZones ?? []
    );
  }
);

/**
 * Zone fee for a map config, flat fee for one without zones, null when neither
 * resolves -- the same three cases the reducer used to write into the slice.
 */
export const selectDeliveryFees: (state: RootState) => number | null =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return null;
      }

      return config.usesMap ? zone?.deliveryFees ?? null : config.deliveryFees;
    }
  );

/**
 * 30 and 45 are the defaults the slice carried, and several screens read these
 * before any delivery selection exists at all (an item label's prep estimate,
 * the bottom sheet), so they must stay numbers rather than going null.
 */
const DEFAULT_MIN_ESTIMATED_TIME = 30;
const DEFAULT_MAX_ESTIMATED_TIME = 45;

export const selectMinEstimatedTime: (state: RootState) => number =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return DEFAULT_MIN_ESTIMATED_TIME;
      }

      return config.usesMap
        ? zone?.minEstimatedTime ?? DEFAULT_MIN_ESTIMATED_TIME
        : config.minEstimatedTime;
    }
  );

export const selectMaxEstimatedTime: (state: RootState) => number =
  createSelector(
    [selectCurrentDeliveryConfig, selectDeliveryZone],
    (config, zone) => {
      if (!config) {
        return DEFAULT_MAX_ESTIMATED_TIME;
      }

      return config.usesMap
        ? zone?.maxEstimatedTime ?? DEFAULT_MAX_ESTIMATED_TIME
        : config.maxEstimatedTime;
    }
  );

export const selectDeliveryZoneId: (state: RootState) => number | null =
  createSelector([selectDeliveryZone], (zone) => zone?.deliveryZoneId ?? null);
